import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSidebar } from '../../../context/SidebarContext';
import { useFeaturedArcheMeta } from '../hooks/useFeaturedArcheMeta';
import { FeaturedArcheRow } from '../components/FeaturedArcheRow';
import { ComingSoonRow } from '../components/ComingSoonRow';
import { FEATURED_ARCHES, COMING_SOON_ARCHES } from '../data/featured-arches';

/** Header cell for the emphasized "Replaces" column — the investor story. */
function ReplacesHead() {
  return (
    <TableHead className="bg-primary/[0.04]">
      <div className="font-semibold text-foreground">Replaces</div>
      <div className="text-[11px] font-normal normal-case text-muted-foreground">
        subscriptions you can cancel
      </div>
    </TableHead>
  );
}

/**
 * Full-page "Add Featured" chooser, reached from the worktop header. A table of
 * the first-party arches that replace common subscriptions — with the
 * "Replaces" column emphasized — followed by a "Coming soon" table of
 * categories we're building next. Doubles as a one-stop-shop overview for
 * users and investors.
 */
export function FeaturedPage() {
  const navigate = useNavigate();
  const { isCollapsed, toggle } = useSidebar();
  const { metaById } = useFeaturedArcheMeta();

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-semibold">Add Featured</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/a/arche_favorites')}
            className="gap-1.5 [&_svg]:size-5!"
          >
            <ArrowLeft />
            Back to Worktop
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-6xl">
          {/* Intro */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              One home for everything you do
            </h2>
            <p className="mt-2 max-w-3xl text-base text-muted-foreground">
              Fias is a one-stop shop to replace your expensive subscriptions and make all of your
              data accessible for AI-enhanced work. Everything below runs in one place — with your
              data kept private in your Vault.
            </p>
            <p className="mt-3 text-sm font-medium text-foreground/70">
              {FEATURED_ARCHES.length} tools available today · {COMING_SOON_ARCHES.length} more on
              the way
            </p>
          </div>

          {/* Available now */}
          <section aria-labelledby="featured-available">
            <h3 id="featured-available" className="mb-3 text-sm font-semibold text-foreground">
              Available now
            </h3>
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="hidden md:table-cell">What it does</TableHead>
                    <ReplacesHead />
                    <TableHead className="hidden md:table-cell">Security</TableHead>
                    <TableHead className="hidden text-right md:table-cell">Worktop</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {FEATURED_ARCHES.map((arche) => (
                    <FeaturedArcheRow
                      key={arche.archId}
                      arche={arche}
                      meta={metaById.get(arche.archId)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          {/* Coming soon */}
          <section aria-labelledby="featured-coming-soon" className="mt-10">
            <h3
              id="featured-coming-soon"
              className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground"
            >
              <Sparkles className="h-4 w-4 text-primary" />
              Coming soon
            </h3>
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="hidden md:table-cell">What it will do</TableHead>
                    <ReplacesHead />
                    <TableHead className="hidden text-right md:table-cell">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {COMING_SOON_ARCHES.map((arche) => (
                    <ComingSoonRow key={arche.name} arche={arche} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
